/**
 * SavedViewsSection — Bottom of analysis sidebar, shows saved view list.
 *
 * Features:
 * - Clickable items to load a view
 * - Active view highlighted
 * - Context menu for rename/delete on each item
 * - Inline rename via text input
 */
import { useState } from 'react';
import {
  Box,
  Text,
  Group,
  UnstyledButton,
  ActionIcon,
  Menu,
  TextInput,
  Divider,
  Stack,
} from '@mantine/core';
import {
  IconStar,
  IconStarFilled,
  IconDots,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import type { AnalysisView } from '@/types/analysisView';

interface SavedViewsSectionProps {
  views: AnalysisView[];
  activeViewId: string | null;
  onLoad: (view: AnalysisView) => void;
  onRename: (viewId: string, newName: string) => void;
  onDelete: (viewId: string) => void;
}

export function SavedViewsSection({
  views,
  activeViewId,
  onLoad,
  onRename,
  onDelete,
}: SavedViewsSectionProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (view: AnalysisView) => {
    setRenamingId(view.id);
    setRenameValue(view.name);
  };

  const commitRename = () => {
    if (renamingId && renameValue.trim()) {
      onRename(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  };

  const cancelRename = () => {
    setRenamingId(null);
  };

  return (
    <Box>
      <Divider my="xs" />
      <Box px="md" py="xs">
        <Group gap={6} mb="xs">
          <IconStar size={14} color="var(--mantine-color-dimmed)" />
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Saved Views
          </Text>
        </Group>
        {views.length === 0 ? (
          <Text size="xs" c="dimmed" fs="italic">
            Click the star icon to save your first view
          </Text>
        ) : (
        <Stack gap={2}>
          {views.map((view) => {
            const isActive = view.id === activeViewId;
            const isRenaming = view.id === renamingId;

            if (isRenaming) {
              return (
                <TextInput
                  key={view.id}
                  size="xs"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.currentTarget.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') cancelRename();
                  }}
                  maxLength={50}
                  autoFocus
                />
              );
            }

            return (
              <UnstyledButton
                key={view.id}
                onClick={() => onLoad(view)}
                py={6}
                px="xs"
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                  backgroundColor: isActive
                    ? 'var(--rdh-sidebar-active)'
                    : undefined,
                  width: '100%',
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {isActive ? (
                      <IconStarFilled size={14} color="var(--mantine-color-teal-6)" style={{ flexShrink: 0 }} />
                    ) : (
                      <IconStar size={14} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
                    )}
                    <Text
                      size="sm"
                      fw={isActive ? 500 : 400}
                      truncate
                    >
                      {view.name}
                    </Text>
                  </Group>
                  <Menu position="bottom-end" withArrow shadow="sm">
                    <Menu.Target>
                      <ActionIcon
                        variant="subtle"
                        size="xs"
                        color="gray"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        aria-label={`Options for ${view.name}`}
                      >
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          startRename(view);
                        }}
                      >
                        Rename
                      </Menu.Item>
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${view.name}"?`)) {
                            onDelete(view.id);
                          }
                        }}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
        )}
      </Box>
    </Box>
  );
}
